import { MobileHelper } from "./../../shared/helpers/mobile-helper";
import { DocumentEvents } from "./../helpers/document-events";
import { GenericDownloadView } from "./partials/generic-download";
import { TemplateManager } from "./../../shared/helpers/template-manager";
import { TabsCollection } from "./../collections/tabs-collection";
import { FullTextPanelView } from "./full-text-panel";
import { PanelTabsView } from "./panel-tabs";
import { IDocumentModel } from "./../interfaces/document-model";
import { GenericTabModel } from "./../models/tabs/generic-tab";
import "backbone";
import "underscore";
import Marionette from "../../shared/marionette-mustache";
import { ViewMode } from "../../desktop/models/document-state";
import { AppEvents } from "../../app-events";
import { transient, Container } from "aurelia-dependency-injection";
import { TabFactory } from "../helpers/tab-factory";
import { ContainerHelper } from "../../shared/helpers/container-helper";

export interface IPlotResult {
    Year: number;
    Percent: number;
}

export enum PdfLinkStatus { // PDF link statuses
    Ok,
    MissingLink,
    MissingFile,
    DummyFile
}

const dom = {
    MORE_BUTTON: ".category-more-button",
    SHOW_DATA_TABLE: ".category-show-data-table",
    JURISDICTION_FILTER_MESSAGE: ".category-jurisdiction-message",
    RELATION_TEXT_FILTER: ".relation-text-filter",
    LEGISLATIONS : ".treatment-cell",
    NO_MATCH_FOUND: ".no-matches-found",
    CATEGORY_TREND: ".category-trend",
    ANALYSIS_SECTIONS: ".analysis-section",
    FULLTEXT_SECTIONS: ".fulltext-section",
    DOCUMENT_SECTIONS: ".document-section",
    SECTION_BUTTONS: ".section-button",
    FULLTEXT_SECTION_BUTTONS: ".full-text-button",
    FACE: ".document-face",
    DOCUMENT_BODY: ".document-body",
    DOCUMENT_HEADER: ".document-header",
    PRECEDENT_MAP_BUTTON: ".precedent-map-button",
    MISSING_TEXT_PDF_LINK: ".linktopdf",
    SWITCH_BUTTONS: ".switch-button",
    PROCESSING_STATUS_CONTAINER : ".document-processing-status",
    PROCESSING_LINK : ".processing-link",
    ANALYSIS_NAV: "ul.analysis-nav",
    FULLTEXT_NAV: "ul.fulltext-nav",
    PRIORITY_PROCESS_BTN: ".priority-process",
    ANALYSIS_NAV_RIGHT_SCROLL_ARROW: ".document-face.analysis .primary-navbar .navbar-inner .nav-right-arrow",
    ANALYSIS_NAV_LEFT_SCROLL_ARROW: ".document-face.analysis .primary-navbar .navbar-inner .nav-left-arrow",
    FULLTEXT_NAV_RIGHT_SCROLL_ARROW: ".document-face.full-text .primary-navbar .navbar-inner .nav-right-arrow",
    FULLTEXT_NAV_LEFT_SCROLL_ARROW: ".document-face.full-text .primary-navbar .navbar-inner .nav-left-arrow",
    PRINT_FULLTEXT_BUTTON: ".print-fulltext-button"
};

@transient()
export class GenericDocumentView extends Marionette.LayoutView<IDocumentModel> {

    public model: IDocumentModel;
    protected templateManager: TemplateManager;
    protected downloadRegion: Marionette.Region;
    protected downloadView: GenericDownloadView;
    protected appEvents: Backbone.Events;
    private fulltextRegion: Marionette.Region;
    private analysisRegion: Marionette.Region;

    constructor(options?: any) {
        super(options);
        this.$el = $(this.el);
        this.appEvents = ContainerHelper.Events;
        this.templateManager = Container.instance.get(TemplateManager);
        this.setupModelListeners();
    }

    public regions(): any {
        const regions: any = {
            analysisRegion: ".analysis-panel-region",
            fulltextRegion: ".full-text-panel-region",
            downloadRegion: ".download-region"
        };
        return regions;
    }

    public onShow() {
        this.setSize();
        this.setupSubViews().then(() => {
            this.model.setUIStateOptions();
            this.setupPopovers();
            this.updatePdfStatusMessage();
            this.updateSwitchButton(this.model.uistate.ViewMode, false);
            this.setupAnalysisNavBarScrolling();
            this.setupFulltextNavBarScrolling();

            if (this.model.NoIndex) {
                this.addNoIndexTagToHead();
            }

            const mh: MobileHelper = Container.instance.get(MobileHelper); // Can't use the class reference to this, guess that code hasn't run to initialise it at this point
            if (mh.IsMobileMode) {
                this.doMobileExtras();
            }
        });
    }

    public doMobileExtras() {
        if (!this.model.HasFullText && !this.model.HasReports) {
            this.$(dom.SWITCH_BUTTONS).prop("disabled", true);
        }
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events[`click ${dom.SECTION_BUTTONS}`] = (e) => this.sectionButtonClick(e);
        events[`click ${dom.SWITCH_BUTTONS}`] = (e) => this.model.uistate.flip();
        events[`click ${dom.PRECEDENT_MAP_BUTTON}`] = (e) => this.precedentMapButtonClick(e);
        events["click " + dom.PRIORITY_PROCESS_BTN] = (e) => this.priorityProcessClick(e);
        events[`click ${dom.PRINT_FULLTEXT_BUTTON}`] = (e) => this.printFullTextClick(e);

        // Analysis navbar scroll stuff
        let analysisLeftArrowTimer;
        let analysisRightArrowTimer;
        events["click " + dom.ANALYSIS_NAV_LEFT_SCROLL_ARROW] = (e) => this.model.uistate.AnalysisNavBarPosition = "left";
        events["mouseenter " + dom.ANALYSIS_NAV_LEFT_SCROLL_ARROW] = (e) => analysisLeftArrowTimer = setTimeout(() => this.model.uistate.AnalysisNavBarPosition = "left", 300);
        events["mouseleave " + dom.ANALYSIS_NAV_LEFT_SCROLL_ARROW] = (e) => clearTimeout(analysisLeftArrowTimer);
        events["click " + dom.ANALYSIS_NAV_RIGHT_SCROLL_ARROW] = (e) => this.model.uistate.AnalysisNavBarPosition = "right";
        events["mouseenter " + dom.ANALYSIS_NAV_RIGHT_SCROLL_ARROW] = (e) => analysisRightArrowTimer = setTimeout(() => this.model.uistate.AnalysisNavBarPosition = "right", 300);
        events["mouseleave " + dom.ANALYSIS_NAV_RIGHT_SCROLL_ARROW] = (e) => clearTimeout(analysisRightArrowTimer);

        // Fulltext navbar scroll stuff
        let fulltextLeftArrowTimer;
        let fulltextRightArrowTimer;
        events["click " + dom.FULLTEXT_NAV_LEFT_SCROLL_ARROW] = (e) => this.model.uistate.FulltextNavBarPosition = "left";
        events["mouseenter " + dom.FULLTEXT_NAV_LEFT_SCROLL_ARROW] = (e) => fulltextLeftArrowTimer = setTimeout(() => this.model.uistate.FulltextNavBarPosition = "left", 300);
        events["mouseleave " + dom.FULLTEXT_NAV_LEFT_SCROLL_ARROW] = (e) => clearTimeout(fulltextLeftArrowTimer);
        events["click " + dom.FULLTEXT_NAV_RIGHT_SCROLL_ARROW] = (e) => this.model.uistate.FulltextNavBarPosition = "right";
        events["mouseenter " + dom.FULLTEXT_NAV_RIGHT_SCROLL_ARROW] = (e) => fulltextRightArrowTimer = setTimeout(() => this.model.uistate.FulltextNavBarPosition = "right", 300);
        events["mouseleave " + dom.FULLTEXT_NAV_RIGHT_SCROLL_ARROW] = (e) => clearTimeout(fulltextRightArrowTimer);
        return events;
    }

    protected setupDownloadView() {

        const downloadTemplate = this.templateManager.getTemplate("generic-download", this.model.DocumentAccessSku);
        downloadTemplate.then((template) => {
            this.downloadView = new GenericDownloadView({model: this.model, template: template.Raw});
            this.downloadView.template = template.Raw;
            this.downloadRegion.show(this.downloadView);
        });
    }

    protected setSize(): void {
        this.$(dom.FACE).width(this.model.FaceWidth - 10);
    }

    protected priorityProcessClick(e: JQueryEventObject): void {
        const $target = $(e.currentTarget);
        const processName = $target.attr("data-process");
        const id = this.model.Id;
        this.model.runPriorityProcess({ Id: id, processName });
    }

    protected setupModelListeners() {
        this.listenTo(this.model.uistate, "change:CurrentAnalysisSection", this.currentAnalysisSectionChanged);
        this.listenTo(this.model.uistate, "change:CurrentFulltextSection", this.currentFullTextSectionChanged);
        this.listenTo(this.model.uistate, "change:ViewMode", this.viewModeChanged);

        this.listenTo(this.model, "reset", this.render);
        this.listenTo(this.model, "change:PdfLinks", this.updatePdfStatusMessage);
        this.listenTo(this.model, DocumentEvents.DocumentRefreshed, this.documentRefreshed);
        this.listenTo(this.model, DocumentEvents.ShowPowerPara, () => this.showJudgmentTab());
        this.listenTo(this.model, DocumentEvents.ToggleCIC, (cicOptions) => this.showCitationsInContext(cicOptions));
        this.listenTo(this.model, DocumentEvents.HighlightAllQuotedParagraphs, () => this.showJudgmentTab());
        this.listenTo(this.model, DocumentEvents.CiteLineEngaged, () => this.prepareForCiteline());
        this.listenTo(this.model, DocumentEvents.CiteLineDisengaged, () => this.cancelCiteline());

        // Navbar scrolling stuff
        this.listenTo(this.model.uistate, "change:AnalysisTabNeedsScroll", this.showHideAnalysisScrollChevrons);
        this.listenTo(this.model.uistate, "change:FulltextTabNeedsScroll", this.showHideFulltextScrollChevrons);
        this.listenTo(this.model.uistate, "change:AnalysisNavBarPosition", () =>  this.analysisNavBarPositionChanged(true));
        this.listenTo(this.model.uistate, "change:FulltextNavBarPosition", () =>  this.fulltextNavBarPositionChanged(true));
        this.listenTo(this.model, DocumentEvents.GainedFocus, () => _.delay(() => { this.setupAnalysisNavBarScrolling(); this.setupFulltextNavBarScrolling(); }, 100));
        this.listenToOnce(this.model.AnalysisTabs, "add", () => _.delay(() => { this.setupAnalysisNavBarScrolling(); this.setupFulltextNavBarScrolling(); }, 500));
        this.listenTo(this.model, DocumentEvents.CompactChanged,  () => _.delay(() => { this.setupAnalysisNavBarScrolling(); this.setupFulltextNavBarScrolling(); }, 100));
        this.appEvents.on(AppEvents.WindowResized, () => _.delay(() => { this.setupAnalysisNavBarScrolling(); this.setupFulltextNavBarScrolling(); }, 100));
    }

    private showJudgmentTab() {
        const judgmentTab = this.model.FulltextTabs.findWhere({ Identifier: "fulltext-judgment" });
        this.model.uistate.CurrentFulltextSection = judgmentTab;
        this.model.uistate.showFulltext();
    }

    /** Puts the navbar in the correct place based on the ui state values AnalysisTabNeedsScroll and AnalysisNavBarPosition */
    private analysisNavBarPositionChanged(animated = false) {

        const $analysisNav = this.$(".document-face.analysis .primary-navbar .navbar-inner");
        const $nav = $analysisNav.find(".nav");
        const $left = $analysisNav.find(".nav-left-arrow");
        const $right = $analysisNav.find(".nav-right-arrow");

        _.delay(() => {
            if (!this.model.uistate.AnalysisTabNeedsScroll) { // Handle separately because in this case we want to keep arrows hidden.
                this.moveNavBar($nav,  0, animated);
                return;
            }

            if (this.model.uistate.AnalysisNavBarPosition === "left") {
                this.moveNavBar($nav, 0, animated);
                $left.fadeOut();
                $right.fadeIn();
            } else {
                this.moveNavBar($nav, $analysisNav.outerWidth() - this.getAnalysisBarTabWidths() - 15, animated);
                $right.fadeOut();
                $left.fadeIn();
            }
        }, 50 );

    }

    /** Puts the navbar in the correct place based on the ui state values FulltextTabNeedsScroll and FulltextNavBarPosition */
    private fulltextNavBarPositionChanged(animated = false) {

        const $fulltextNav = this.$(".document-face.full-text .primary-navbar .navbar-inner");
        const $nav = $fulltextNav.find(".nav");
        const $left = $fulltextNav.find(".nav-left-arrow");
        const $right = $fulltextNav.find(".nav-right-arrow");

        _.delay(() => {
            if (!this.model.uistate.FulltextTabNeedsScroll) { // Handle separately because in this case we want to keep arrows hidden.
                this.moveNavBar($nav,  0, animated);
                return;
            }

            if (this.model.uistate.FulltextNavBarPosition === "left") {
                this.moveNavBar($nav, 0, animated);
                $left.fadeOut();
                $right.fadeIn();
            } else {
                this.moveNavBar($nav, $fulltextNav.outerWidth() - this.getFulltextBarTabWidths() - 15, animated);
                $right.fadeOut();
                $left.fadeIn();
            }
        }, 50 );

    }

    private moveNavBar($nav: JQuery, leftPx: number, animate = false ) {
        if (!animate) {
            $nav.addClass("no-transition");
            $nav.css("left", leftPx);
            $nav.outerHeight(); // Necessary to force a reflow - otherwise browser won't add the class before changing the css. See https://stackoverflow.com/questions/11131875/what-is-the-cleanest-way-to-disable-css-transition-effects-temporarily
        } else {
            $nav.css("left", leftPx);
        }

        $nav.removeClass("no-transition");
    }

    /** Calculates whether we should allow the navbar to be scrollable, and sets the UI state variable. */
    private setupAnalysisNavBarScrolling() {

        const $analysisNav = this.$(".document-face.analysis .primary-navbar .navbar-inner");

        const availableWidth = $analysisNav.outerWidth();
        const tabWidths = this.getAnalysisBarTabWidths();

        if (tabWidths > availableWidth) {
            this.model.uistate.AnalysisTabNeedsScroll = true;
            this.analysisNavBarPositionChanged();
        } else {
            this.model.uistate.AnalysisTabNeedsScroll = false;
            this.analysisNavBarPositionChanged();
        }
    }

    /** Calculates whether we should allow the navbar to be scrollable, and sets the UI state variable. */
    private setupFulltextNavBarScrolling() {

        const $analysisNav = this.$(".document-face.analysis .primary-navbar .navbar-inner");

        const availableWidth = $analysisNav.outerWidth();
        const tabWidths = this.getFulltextBarTabWidths();

        if (tabWidths > availableWidth) {
            this.model.uistate.FulltextTabNeedsScroll = true;
            this.fulltextNavBarPositionChanged();
        } else {
            this.model.uistate.FulltextTabNeedsScroll = false;
            this.fulltextNavBarPositionChanged();
        }
    }

    private getAnalysisBarTabWidths() {
        const $analysisNav = this.$(".document-face.analysis .primary-navbar .navbar-inner");

        const availableWidth = $analysisNav.width();
        const tabElements = $analysisNav.find(".nav").children();
        let tabWidths = 0;
        tabElements.each((index, element) => {
            tabWidths += $(element).width();
        });
        return tabWidths;
    }

    private getFulltextBarTabWidths() {
        const $fulltextNav = this.$(".document-face.full-text .primary-navbar .navbar-inner");

        const availableWidth = $fulltextNav.width();
        const tabElements = $fulltextNav.find(".nav").children();
        let tabWidths = 0;
        tabElements.each((index, element) => {
            tabWidths += $(element).width();
        });
        return tabWidths;
    }

    private showHideAnalysisScrollChevrons(model, prop) {

        const $analysisNav = this.$(".document-face.analysis .primary-navbar .navbar-inner");
        const $left = $analysisNav.find(".nav-left-arrow");
        const $right = $analysisNav.find(".nav-right-arrow");

        if (this.model.uistate.AnalysisTabNeedsScroll) {
            if (this.model.uistate.AnalysisNavBarPosition === "left") {
                $right.show();
            } else {
                $left.show();
            }
        } else {
            $left.hide();
            $right.hide();
        }
    }

    private showHideFulltextScrollChevrons(model, prop) {

        const $fulltextNav = this.$(".document-face.full-text .primary-navbar .navbar-inner");
        const $left = $fulltextNav.find(".nav-left-arrow");
        const $right = $fulltextNav.find(".nav-right-arrow");

        if (this.model.uistate.FulltextTabNeedsScroll) {
            if (this.model.uistate.FulltextNavBarPosition === "left") {
                $right.show();
            } else {
                $left.show();
            }
        } else {
            $left.hide();
            $right.hide();
        }
    }

    private prepareForCiteline() {
        this.$(".document-template").addClass("citeline-engaged");
        this.$(".document-nav").fadeOut();
        this.$(".document-face").addClass("moses");
    }

    private cancelCiteline() {
        this.$(".document-template").removeClass("citeline-engaged");
        this.$(".document-nav").fadeIn();
        this.$(".document-face").removeClass("moses");
    }

    private documentRefreshed() {
        this.model.AnalysisTabs.forEach((t) => {
            t.setDocumentData(this.model);
        });
        this.model.FulltextTabs.forEach((t) => {
            t.setDocumentData(this.model);
        });
    }

    private viewModeChanged() {
        const model = this.model.uistate;
        const mode = model.ViewMode;

        const sectionName = model.CurrentAnalysisSection ? model.CurrentAnalysisSection.Identifier : "overview";
        const isCategoryDocumentView = (sectionName === "leading-cases" || sectionName === "recent-cases" || sectionName === "important-legislation");

        this.updateSwitchButton(mode, isCategoryDocumentView);

        if (mode === ViewMode.Precedentmap) {
            this.initPrecedentMap();
        } else {
            this.$(dom.DOCUMENT_BODY).toggleClass("flip", mode === ViewMode.FullText); // defer prevents intermittent issues in some browsers

            // trigger an event on this document view"s document holder if the view mode isn"t blank
            if (mode && mode !== ViewMode.Blank) {
                try {
                    (this.model.collection as any).parents[0].trigger("viewModeChanged", mode);
                } catch (err) {
                    dev.log("trigger went wrong");
                }
            }

            // This will get called when switching stages, use it to update navbar scrolling
            _.delay(() => { this.setupAnalysisNavBarScrolling(); this.setupFulltextNavBarScrolling(); }, 100);
        }
    }

    /** Handles UI updates when section changed on  analysis panel. */
    private currentAnalysisSectionChanged(model, tab: GenericTabModel): void {
        const collection = this.model.AnalysisTabs;
        if (!tab || !collection) {
            return;
        }

        collection.where((t) => t.Active).forEach((t) => {
            t.Active = false;
        });
        tab.Active = true;
        const selector = `${dom.SECTION_BUTTONS}[data-section='${tab.Identifier}']`;
        const button = this.$(selector).parent().addClass("active");
        this.$(`${dom.ANALYSIS_NAV} ${dom.SECTION_BUTTONS}`).parents().not(button).not(".document-holder").removeClass("active");
    }

    private currentFullTextSectionChanged(model, tab: GenericTabModel): void {
        const collection = this.model.FulltextTabs;
        if (!tab || !collection) {
            return;
        }
        collection.where((t) => t.Active).forEach((t) => {
            t.Active = false;
        });
        tab.Active = true;
        const selector = `${dom.SECTION_BUTTONS}[data-section='${tab.Identifier}']`;
        const button = this.$(selector).parent().addClass("active");
        this.$(`${dom.FULLTEXT_NAV} ${dom.SECTION_BUTTONS}`).parents().not(button).not(".document-holder").removeClass("active");

        if (tab.TemplateName === "fulltext") {
            this.$(dom.PRINT_FULLTEXT_BUTTON).show();
        } else {
            this.$(dom.PRINT_FULLTEXT_BUTTON).hide();
        }
    }

    private async setupSubViews() {
        await this.setupAnalysisPanel();
        await this.setupFullTextPanel();
    }

    private setupAnalysisPanel() {
        return new Promise((resolve) => {
            if (this.model.AnalysisTabs.length === 0) { // Get tabs if we don't already have
                TabFactory.GetAnalysisTabs(this.model).then((tabs) => {
                    this.model.AnalysisTabs.add(tabs);
                    this.createAndShowAnalysisView();
                    resolve();
                });
            } else {
                this.createAndShowAnalysisView();
                resolve();
            }
        });
    }

    private setupFullTextPanel() {
        return new Promise((resolve) => {
            if (this.model.FulltextTabs.length === 0 ) {
                TabFactory.GetFullTextTabs(this.model).then((tabs) => { // Change this to get judgment panel first, then later add in the report tabs
                    this.model.FulltextTabs.add(tabs);
                    this.createAndShowFulltextView();
                    this.model.trigger(DocumentEvents.FullTextLoaded);
                    resolve();
                });
            } else {
                this.createAndShowFulltextView();
                resolve();
            }
        });
    }

    private createAndShowFulltextView() {
        const fullTextView = new FullTextPanelView(this.model, {collection: this.model.FulltextTabs});
        this.fulltextRegion.show(fullTextView);
        this.addTabsToNavBar(this.model.FulltextTabs, dom.FULLTEXT_NAV);
        _.delay(() => { this.setupFulltextNavBarScrolling(); }, 100);

        this.setupDownloadView(); // must be done once fulltext is loaded to add reports to downloader
    }

    private createAndShowAnalysisView() {
        // Show analysis region
        const analysisView = new PanelTabsView({collection: this.model.AnalysisTabs});
        this.analysisRegion.show(analysisView);
        this.addTabsToNavBar(this.model.AnalysisTabs, dom.ANALYSIS_NAV);
        _.delay(() => { this.setupAnalysisNavBarScrolling(); }, 100);
    }

    private addTabsToNavBar(tabs: TabsCollection, navbarSelector: string) {
        tabs.forEach((t) => {
            this.addTabToNavbar(t, navbarSelector);
        });
        const first = tabs.first();
        if (first) {
            first.Active = true;
        }

    }

    private hideAllPopovers() {
        $(".paragraph-controls").popover("destroy");
        $(".popover").popover("hide");
    }

    private addNoIndexTagToHead() {
        const $existing = $("head").find("meta[name=robots]");
        if ($existing.length > 0) {
            return; // the tag already exists on the page, don't put it in twice
        }

        const $robotsTag = $('<meta name="robots" content="noindex">');
        $("head").append($robotsTag);
    }

    private addTabToNavbar(model: GenericTabModel, navBarSelector: string) {
        this.$(navBarSelector).append(model.TitleHtml);
    }

    private processingLinkClick(e) {
        e.preventDefault();
        this.$(dom.PROCESSING_STATUS_CONTAINER).popover("hide");
        const url = $(e.currentTarget).attr("href");
        Backbone.history.navigate(url, true);
    }

    private setupPopovers() {
        this.$(dom.MISSING_TEXT_PDF_LINK).popover({
            trigger: "hover focus",
            placement: "bottom",
            // need to add an id to the default template to use aria-describedby
            template: "<div id='linktopdf-popover' class='popover' role='tooltip'><div class='arrow'></div><h3 class='popover-title'></h3><div class='popover-content'></div></div>"
        });
    }

    private sectionButtonClick(e: JQueryEventObject): void {
        e.stopPropagation();
        const $section = this.$(e.target);
        const sectionID = $section.data("section");
        if ($section.hasClass("disabled") && $section.data("panel").toLowerCase() === "fulltext") { // We're allowed to click disabled analysis panels, but not on fulltext side
            return;
        }

        const url = this.model.UrlTemplate.replace("{section}", sectionID);

        Backbone.history.navigate(url, {trigger: true});
    }

    private updatePdfStatusMessage(): void {
        const pdfs = this.model.PdfLinks || [];
        const pdfStatusContainer = this.$el.find("#js-pdf-status-container");

        // get status
        let pdfStatus = PdfLinkStatus.MissingFile; // assume missing
        for (const pdf of pdfs) {
            if (pdf.PdfStatus === PdfLinkStatus.Ok) {
                pdfStatus = PdfLinkStatus.Ok;
                break;
            }
        }

        let pdfStatusClass = "fa fa-file pdf-status";
        pdfStatusClass += (pdfStatus === PdfLinkStatus.Ok) ? " pdf-status-ok" : " pdf-status-missing";

        // get message
        let pdfMsgs = "";
        for (const pdf of pdfs) {
            pdfMsgs += (pdf.Message === null) ? "Pdf available" : pdf.Message;
            pdfMsgs += " : ";
            pdfMsgs +=  (pdf.PdfStatus === PdfLinkStatus.MissingLink) ? "" : pdf.PdfLink + "\n";
        }

        if (pdfMsgs === "") {
            pdfMsgs += "Pdf not available";
        }

        const html = $("<i>").addClass(pdfStatusClass).attr("title", pdfMsgs);
        pdfStatusContainer.empty().append(html);
    }

    private precedentMapButtonClick(ev: JQueryEventObject): void {
        this.appEvents.trigger(AppEvents.AppInsightsDocumentPrecedentMapOpen, {
            Title: this.model.FriendlyUrlTitle,
            url: this.model.Url
        });
        this.hideAllPopovers();
        ev.stopPropagation();
        this.initPrecedentMap();
    }

    private initPrecedentMap(): void {
        const url = `precedent-map/${this.model.FriendlyUrlTitle}/${this.model.Id}/${this.model.UniqueId}`;
        Backbone.history.navigate(url, { trigger: true });
    }

    private updateSwitchButton(currentMode: ViewMode, isCategoryDocuments: boolean) {

        let otherMode: ViewMode;
        if (currentMode === ViewMode.FullText) {
            otherMode = ViewMode.Analysis;
        }
        if (currentMode === ViewMode.Analysis) {
            otherMode = ViewMode.FullText;
        }

        this.$(dom.SWITCH_BUTTONS).attr("data-mode", otherMode);

        let buttonText;
        if (isCategoryDocuments) {
            buttonText = (otherMode === ViewMode.Analysis) ? "Overview" : "Analysis" ;
        } else {
            buttonText = (otherMode === ViewMode.Analysis) ? "Overview" : "Full Text";
        }

        this.$(dom.SWITCH_BUTTONS).text(buttonText);
    }

    private showCitationsInContext(cicOptions) {
        cicOptions.Active = !cicOptions.Active;
        cicOptions.button.attr("aria-pressed", cicOptions.Active);
        const judgmentTab = this.model.FulltextTabs.findWhere({Identifier: "fulltext-judgment"});
        if (cicOptions.Active) {
            this.model.uistate.CurrentFulltextSection = judgmentTab;
        }
        this.model.uistate.showFulltext();
    }

    private printFullTextClick(e: JQueryEventObject) {
        const docId = $(e.currentTarget).attr("id");
        this.appEvents.trigger(AppEvents.PrintFullText, docId, false);
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/generic-document.ts